import {
    type AccountLoginOrRegisterSuccessResponse,
    type AccountRegisterParams,
    type EnrollProfileResponse,
    logSentryError,
    type Profile,
    SentryAcpTreatsRewardsErrors,
} from '@libs/common-next';
import { AUTH_HEADERS, ensureAuth0AccessToken, getAccessToken } from '@libs/core';

import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';
import {
    getAnonymousIdToken,
    getCartIdToken,
    removeAnonymousIdToken,
    removeCartIdToken,
    setAnonymousIdToken,
    setCartCookie,
} from '../utils';
import { unwrapApiResponseOrThrow } from './utils';

export type AnonymousId = {
    anonymousId?: string;
};

export async function getAnonymousId(): Promise<string | undefined> {
    try {
        const currentAnonymousId = getAnonymousIdToken();
        const isLoggedIn = getAccessToken();

        if (!currentAnonymousId && !isLoggedIn) {
            // change full fetch url to https://qa.in.nonprod.petsmartcloud.com/ecom/customer-web-experience/api/account/anonymousId to test locally
            const response = await cweaClient.post<AnonymousId>(CWEA_ENDPOINTS.account.anonymousId, {
                useOldFetch: true,
            });

            const newAnonymousId = response?.anonymousId;
            if (!newAnonymousId) {
                throw new Error('Unable to get anonymous ID');
            }

            removeCartIdToken();

            setAnonymousIdToken(newAnonymousId);
            return newAnonymousId;
        }

        return currentAnonymousId;
    } catch (e) {
        console.error(e);
    }
}

export async function loginOrRegister(body?: AccountRegisterParams) {
    const anonymousCartId = getCartIdToken();
    try {
        const resCartMerge = await cweaClient.post<
            AccountLoginOrRegisterSuccessResponse,
            Partial<AccountRegisterParams>
        >(CWEA_ENDPOINTS.account.accounts, {
            body: {
                ...body,
                anonymousCartId,
            },
            additionalSuccessCodes: [400, 401, 404],
            useOldFetch: true,
        });

        const mergedCartId = resCartMerge?.updatedCart?.id ?? resCartMerge?.cart?.id;
        if (mergedCartId) {
            setCartCookie(mergedCartId);
        }

        removeAnonymousIdToken();

        return resCartMerge;
    } catch (e) {
        console.error(e);
    }
}

export async function enrollProfile(key: string, options?: { auth0Enabled?: boolean }) {
    return await cweaClient.get<EnrollProfileResponse>(CWEA_ENDPOINTS.account.enrollmentProfile(key), {
        useOldFetch: true,
        ...(options?.auth0Enabled ? { headers: { [AUTH_HEADERS.X_PETM_AUTH0_ENABLED]: 'true' } } : {}),
    });
}

export async function getProfile() {
    const accessToken = getAccessToken();
    if (!accessToken) {
        return null;
    }

    const url = CWEA_ENDPOINTS.profile.getProfile;
    const response = await cweaClient.get<Profile>(url, {
        headers: {
            [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${accessToken}`,
        },
    });

    return response;
}

export async function getOnboardingCompleted(): Promise<boolean> {
    const accessToken = getAccessToken();

    if (!accessToken) {
        return false;
    }

    try {
        const response = await cweaClient.get<{ treatsRewardsOnboardingCompleted: boolean }>(
            CWEA_ENDPOINTS.account.onboarding,
            {
                headers: {
                    [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${accessToken}`,
                },
            }
        );
        const data = unwrapApiResponseOrThrow(response);
        return data?.treatsRewardsOnboardingCompleted ?? false;
    } catch {
        return false;
    }
}

export async function setOnboardingCompleted(): Promise<void> {
    const accessToken = await ensureAuth0AccessToken();

    if (!accessToken) {
        return;
    }

    try {
        await cweaClient.put(CWEA_ENDPOINTS.account.onboarding, {
            headers: {
                [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${accessToken}`,
            },
            body: { treatsRewardsOnboardingCompleted: true },
        });
    } catch (err) {
        logSentryError(err, SentryAcpTreatsRewardsErrors.OnboardingTooltipProvider.SetOnboardingCompletedFailed);
    }
}
